import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, Grid, IconButton,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSolicitudRequerimientoStore } from '../store/solicitudRequerimientoStore';
import { SolicitudRequerimientoService } from '../services/solicitudRequerimientoService';

export const SolicitudRequerimientoFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id && id !== 'nueva';
  const { selectedItem, loading, fetchById, save } = useSolicitudRequerimientoStore();

  const [formData, setFormData] = useState<any>({
    idSolicitudRequerimiento: 0, numero: '', asunto: '', observaciones: '',
    estado: 'PEN', detalles: [],
  });

  useEffect(() => { if (isEdit && id) fetchById(parseInt(id)); }, [id]);
  useEffect(() => {
    if (isEdit && selectedItem) setFormData({ ...selectedItem, detalles: selectedItem.detalles || [] });
  }, [selectedItem]);

  const handleChange = (field: string, value: any) =>
    setFormData((prev: any) => ({ ...prev, [field]: value }));

  const handleDetalleChange = (index: number, field: string, value: any) => {
    setFormData((prev: any) => {
      const detalles = [...prev.detalles];
      detalles[index] = { ...detalles[index], [field]: value };
      return { ...prev, detalles };
    });
  };

  const addDetalle = () =>
    setFormData((prev: any) => ({
      ...prev, detalles: [...prev.detalles, { idArticulo: null, descripcion: '', unidadMedida: '', cantidad: 1 }],
    }));

  const removeDetalle = (index: number) =>
    setFormData((prev: any) => ({ ...prev, detalles: prev.detalles.filter((_: any, i: number) => i !== index) }));

  const handleSubmit = async () => {
    try { await save(formData); navigate('/compras/solicitud-requerimiento'); } catch { /* handled */ }
  };

  const handleAprobar = async () => {
    if (!isEdit || !id) return;
    try {
      await SolicitudRequerimientoService.aprobar(parseInt(id), 0);
      navigate('/compras/solicitud-requerimiento');
    } catch { /* handled */ }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/compras/solicitud-requerimiento')}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" fontWeight="bold">
          {isEdit ? 'Editar Solicitud de Requerimiento' : 'Nueva Solicitud de Requerimiento'}
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Datos Generales</Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField label="Numero" size="small" fullWidth value={formData.numero || ''} onChange={(e) => handleChange('numero', e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField label="Fecha Solicitud" size="small" fullWidth type="date" InputLabelProps={{ shrink: true }}
              value={formData.fechaSolicitud?.substring(0, 10) || new Date().toISOString().substring(0, 10)}
              onChange={(e) => handleChange('fechaSolicitud', e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Asunto" size="small" fullWidth value={formData.asunto || ''} onChange={(e) => handleChange('asunto', e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField label="Observaciones" size="small" fullWidth multiline rows={2} value={formData.observaciones || ''} onChange={(e) => handleChange('observaciones', e.target.value)} />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Detalle de Items</Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addDetalle} size="small">Agregar Linea</Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={50}>#</TableCell>
                <TableCell>Descripcion</TableCell>
                <TableCell width={120}>U.M.</TableCell>
                <TableCell width={120}>Cantidad</TableCell>
                <TableCell width={50}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formData.detalles.map((det: any, idx: number) => (
                <TableRow key={idx}>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>
                    <TextField size="small" fullWidth value={det.descripcion || ''} onChange={(e) => handleDetalleChange(idx, 'descripcion', e.target.value)} />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" fullWidth value={det.unidadMedida || ''} onChange={(e) => handleDetalleChange(idx, 'unidadMedida', e.target.value)} />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" type="number" fullWidth value={det.cantidad || 0} onChange={(e) => handleDetalleChange(idx, 'cantidad', parseFloat(e.target.value) || 0)} />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" color="error" onClick={() => removeDetalle(idx)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {formData.detalles.length === 0 && (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>Sin lineas de detalle</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        {isEdit && formData.estado === 'PEN' && (
          <Button variant="outlined" color="success" startIcon={<CheckCircleIcon />} onClick={handleAprobar}>Aprobar</Button>
        )}
        <Button variant="outlined" onClick={() => navigate('/compras/solicitud-requerimiento')}>Cancelar</Button>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSubmit} disabled={loading}>
          {loading ? 'Guardando...' : 'Guardar'}
        </Button>
      </Box>
    </Box>
  );
};
